import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { nativeAppUrl, androidAppDownloadUrl, iosAppDownloadUrl } from "../../shared/utils/native-app-url.utils";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpErrorResponse } from "@angular/common/http";
import {
  PublicEventInviteParticipantResponse,
  PublicEventInvitePreviewResponse,
  PublicEventInviteChatMessageResponse,
  PublicEventRsvpRequest,
  PublicEventRsvpResponse,
  PublicEventRsvpResponseType
} from "../../shared/models/guestOwner";
import { SupportedLanguage } from "../../shared/models/player";
import { GuestOwnerService, PublicEventInviteStreamEvent } from "../../shared/services/guest-owner.service";
import { GuestUpgradeClaimService } from "../../shared/services/guest-upgrade-claim.service";
import { GuestContinuationObserverService } from "../../shared/services/guest-continuation-observer.service";
import { LocaleService } from "../../shared/i18n/locale.service";
import { BehaviorSubject, of, Subscription } from "rxjs";
import { catchError, filter, finalize, switchMap, take, tap, timeout } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "@auth0/auth0-angular";
import { EventService } from "../../shared/services/event.service";
import { PlayerService } from "../../shared/services/player.service";
import { ChatService } from "../../shared/services/chat.service";
import { GuestParticipantListItem } from "../guest-flow/guest-participant-list.component";

@Component({
    selector: "app-public-event-invite",
    templateUrl: "./public-event-invite.component.html",
    styleUrls: ["../event.component.css", "../guest-owner/guest-owner.component.css"],
    standalone: false
})
export class PublicEventInviteComponent implements OnDestroy, OnInit {
  private static readonly ANDROID_APP_URL = androidAppDownloadUrl;
  private static readonly IOS_APP_URL = iosAppDownloadUrl;
  private static readonly DEFAULT_MANAGER_NAMES = new Set(["organizator", "organizer"]);

  public inviteToken: string;
  public invite: PublicEventInvitePreviewResponse;
  public participants: PublicEventInviteParticipantResponse[] = [];
  public name = "";
  public previewLoading = false;
  public previewLoadingVisible = false;
  public participantsLoading = false;
  public submitting = false;
  public selectedResponse: PublicEventRsvpResponseType;
  public savedResponse: PublicEventRsvpResponseType;
  public savedName = "";
  public editingName = false;
  public responseSavedAt: Date;
  public readonly inviteReady$ = new BehaviorSubject<boolean>(false);
  public inviteUnavailable = false;
  public inviteUnavailableMsg: string;
  public errorMsg: string;
  public participantListMsg: string;
  public successMsg: string;
  public nameErrorMsg: string;
  public matchTimeChanged = false;
  public matchLocationChanged = false;
  public inviteRealtimeAlive = false;
  public inviteRealtimeError = false;
  public chatBridgeVisible = false;
  public chatPreviewLoading = false;
  public chatPreviewUnavailable = false;
  public publicChatMessages: PublicEventInviteChatMessageResponse[] = [];
  public showLanguageSelection = false;
  public selectedLanguage: SupportedLanguage = "en";
  public isMobileBrowser = false;
  public authenticatedRedirectInProgress = false;
  public authenticatedPlayer: any = null;
  public openingMobileApp = false;
  public guestContinuationClaimed = false;
  public guestContinuationObserverError = false;
  private closePublicInviteStream: () => void;
  private guestContinuationObservation: Subscription;
  private authenticatedInviteJoinAttempted = false;
  private authenticationResolution: Subscription;
  private previewLoadingTimer: number;
  private destroyed = false;
  private participantsRequestSequence = 0;
  private inviteRefreshSequence = 0;
  private inviteRefreshInFlight = false;
  @ViewChild("nameEditor") private nameEditorRef?: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private guestOwnerService: GuestOwnerService,
    private guestUpgradeClaimService: GuestUpgradeClaimService,
    private guestContinuationObserverService: GuestContinuationObserverService,
    private localeService: LocaleService,
    private auth: AuthService,
    private eventService: EventService,
    private playerService: PlayerService,
    private translate: TranslateService,
    private chatService: ChatService
  ) {}

  public ngOnInit(): void {
    this.destroyed = false;
    this.inviteToken = this.route.snapshot.params["inviteToken"];
    this.isMobileBrowser = this.detectMobileBrowser();
    this.redirectAuthenticatedUserToMatch();
  }

  private initializeGuestInvite(): void {
    const cachedLanguage = this.localeService.cachedLanguage();
    if (!cachedLanguage) {
      this.hidePreviewLoading();
      this.selectedLanguage = this.defaultPublicLanguage();
      this.showLanguageSelection = true;
      this.inviteReady$.next(false);
      return;
    }

    this.selectedLanguage = cachedLanguage;
    this.localeService.use(cachedLanguage).subscribe(
      () => this.loadInvite(),
      () => this.loadInvite()
    );
  }

  private redirectAuthenticatedUserToMatch(): void {
    if (this.authenticatedInviteJoinAttempted || !this.inviteToken) {
      return;
    }

    this.authenticatedInviteJoinAttempted = true;
    let guestInitialized = false;
    this.showPreviewLoading();
    this.authenticationResolution = this.auth.isLoading$.pipe(
      filter((loading) => !loading),
      take(1),
      timeout(15000),
      switchMap(() => this.auth.isAuthenticated$.pipe(take(1))),
      tap((isAuthenticated) => {
        if (!isAuthenticated) {
          guestInitialized = true;
          this.initializeGuestInvite();
        }
      }),
      filter((isAuthenticated) => isAuthenticated),
      switchMap(() => this.playerService.currentPlayer$.pipe(
        filter((player) => !!player),
        take(1),
        timeout(15000)
      )),
      tap((player) => {
        this.authenticatedPlayer = player;
      }),
      switchMap(() => this.guestOwnerService.authenticatedInviteMembership(this.inviteToken)),
      switchMap((membership) => {
        if (membership.alreadyJoined) return this.eventService.getEvent(membership.eventId);
        guestInitialized = true;
        this.initializeGuestInvite();
        return of(null);
      }),
      tap((event) => {
        if (event) this.openAuthenticatedMatch(event);
      }),
      catchError(() => {
        this.errorMsg = this.translate.instant("publicInvite.errors.linkUnavailable");
        return of(null);
      }),
      finalize(() => {
        this.authenticatedRedirectInProgress = false;
        if (!guestInitialized) {
          this.hidePreviewLoading();
        }
      })
    ).subscribe();
  }

  public joinWithAccount(): void {
    if (!this.authenticatedPlayer || this.submitting || this.inviteUnavailable) return;
    this.submitting = true;
    this.errorMsg = null;
    this.guestUpgradeClaimService.claimPublicInviteParticipantForToken(this.inviteToken).pipe(
      switchMap((claimResult) => {
        if (claimResult?.attempted) {
          if (!claimResult.success) this.errorMsg = this.translate.instant("publicInvite.errors.linkUnavailable");
          else this.chatService?.changed$.next();
          return of(null);
        }
        return this.guestOwnerService.joinAuthenticatedInvite(this.inviteToken).pipe(
          tap(() => this.chatService?.changed$.next()),
          switchMap((response) => this.eventService.getEvent(response.eventId))
        );
      }),
      catchError(() => {
        this.errorMsg = this.translate.instant("publicInvite.errors.linkUnavailable");
        return of(null);
      }),
      finalize(() => this.submitting = false)
    ).subscribe((event) => {
      if (!event) {
        return;
      }

      this.openAuthenticatedMatch(event);
    });
  }

  private openAuthenticatedMatch(event: any): void {
    localStorage.setItem("event", JSON.stringify(event));
    this.router.navigate(["/event-players", false], { replaceUrl: true });
  }

  public selectLanguage(language: SupportedLanguage): void {
    this.selectedLanguage = language;
    this.showLanguageSelection = false;
    this.localeService.usePublic(language).subscribe(
      () => this.loadInvite(),
      () => this.loadInvite()
    );
  }

  public isSelectedLanguage(language: SupportedLanguage): boolean {
    return this.selectedLanguage === language;
  }

  public showLanguageChooser(): void {
    this.stopPublicInviteStream();
    this.showLanguageSelection = true;
    this.inviteReady$.next(false);
    this.hidePreviewLoading();
    this.errorMsg = null;
  }

  private loadInvite(): void {
    this.showPreviewLoading();
    this.inviteReady$.next(false);
    this.guestOwnerService.previewInvite(this.inviteToken).pipe(
      timeout(15000),
      finalize(() => {
        this.hidePreviewLoading();
        this.inviteReady$.next(true);
      })
    ).subscribe(
      (invite) => {
        this.invite = invite;
        this.inviteUnavailable = false;
        this.startGuestContinuationObserver(invite.eventId);
        this.loadParticipants();
        this.startPublicInviteStream();
      },
      (error: HttpErrorResponse) => {
        if (this.isInviteUnavailableError(error)) {
          this.markInviteUnavailable(this.translate.instant("publicInvite.errors.matchUnavailable"));
          return;
        }

        this.errorMsg = this.translate.instant("publicInvite.errors.linkUnavailable");
      }
    );
  }

  private defaultPublicLanguage(): SupportedLanguage {
    const browserLanguages = typeof navigator !== "undefined" && navigator.languages
      ? navigator.languages
      : [];

    return browserLanguages.some((language) => language && language.toLowerCase().indexOf("hr") === 0)
      ? "hr"
      : "en";
  }

  private detectMobileBrowser(): boolean {
    if (typeof navigator === "undefined") {
      return false;
    }

    const userAgent = navigator.userAgent || "";
    const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const touchDevice = navigator.maxTouchPoints && navigator.maxTouchPoints > 1;

    return mobileUserAgent || !!touchDevice;
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.authenticationResolution?.unsubscribe();
    this.hidePreviewLoading();
    this.stopPublicInviteStream();
    this.stopGuestContinuationObserver();
  }

  private showPreviewLoading(): void {
    this.previewLoading = true;
    this.previewLoadingVisible = false;
    this.clearPreviewLoadingTimer();
    this.previewLoadingTimer = window.setTimeout(() => {
      this.previewLoadingVisible = this.previewLoading;
      this.previewLoadingTimer = null;
    }, 300);
  }

  private hidePreviewLoading(): void {
    this.previewLoading = false;
    this.previewLoadingVisible = false;
    this.clearPreviewLoadingTimer();
  }

  private clearPreviewLoadingTimer(): void {
    if (!this.previewLoadingTimer) {
      return;
    }
    window.clearTimeout(this.previewLoadingTimer);
    this.previewLoadingTimer = null;
  }

  public rsvp(response: PublicEventRsvpResponseType): void {
    this.saveRsvp(response, false, false);
  }

  private saveRsvp(response: PublicEventRsvpResponseType, force: boolean, createProfileAfterSave: boolean): void {
    if (this.inviteUnavailable) {
      return;
    }

    const name = (this.name || "").trim();
    if (!name) {
      this.nameErrorMsg = this.translate.instant("publicInvite.errors.nameRequired");
      return;
    }

    if (!force && this.savedResponse === response && !this.hasNameChanged()) {
      return;
    }

    this.submitting = true;
    this.selectedResponse = response;
    this.errorMsg = null;
    this.nameErrorMsg = null;
    this.successMsg = null;
    const participantId = this.storedParticipantId();
    const submitRsvp = (storedParticipantId: string) => {
      const request: PublicEventRsvpRequest = { name, response };
      if (storedParticipantId) {
        request.participantId = storedParticipantId;
      }
      return this.guestOwnerService.rsvp(this.inviteToken, request);
    };

    submitRsvp(participantId).pipe(
      catchError((error: HttpErrorResponse) => {
        if (participantId && this.isStoredParticipantRejected(error)) {
          this.clearStoredParticipantId();
          return submitRsvp("");
        }
        throw error;
      }),
      finalize(() => {
        this.submitting = false;
      })
    ).subscribe(
      (rsvpResponse) => {
        if (rsvpResponse && rsvpResponse.participantId) {
          this.storeParticipantId(rsvpResponse.participantId);
          this.storeParticipantClaim(rsvpResponse, name, response);
          this.upsertParticipant({
            participantId: rsvpResponse.participantId,
            name: rsvpResponse.name || name,
            response: rsvpResponse.response || response,
            matchOwner: rsvpResponse.matchOwner,
            source: rsvpResponse.source
          });
        }
        this.markRsvpSaved(
          rsvpResponse && rsvpResponse.response ? rsvpResponse.response : response,
          name
        );
        this.loadParticipants(false);
        if (createProfileAfterSave) {
          this.navigateToGuestUpgrade();
        }
      },
      (error: HttpErrorResponse) => {
        if (this.successMsg) {
          return;
        }
        if (this.isInviteUnavailableError(error)) {
          this.markInviteUnavailable(this.translate.instant("publicInvite.errors.matchUnavailable"));
          return;
        }
        this.errorMsg = this.errorMessage(error);
      }
    );
  }

  public inviteTime(): string {
    if (!this.invite || !this.invite.startDateTime) {
      return this.translate.instant("events.timeNotSet");
    }

    const start = new Date(this.invite.startDateTime);
    if (isNaN(start.getTime())) {
      return this.translate.instant("events.timeNotSet");
    }

    const date = start.toLocaleDateString(this.browserLocale(), { dateStyle: "full" });
    const time = start.toLocaleTimeString(this.browserLocale(), { timeStyle: "short" });
    return this.translate.instant("events.dateTimeAt", { date, time });
  }

  public inviteDate(): string {
    if (!this.invite || !this.invite.startDateTime) {
      return this.translate.instant("events.timeNotSet");
    }

    const start = new Date(this.invite.startDateTime);
    if (isNaN(start.getTime())) {
      return this.translate.instant("events.timeNotSet");
    }

    return start.toLocaleDateString(this.browserLocale(), { weekday: "long", day: "numeric", month: "long" });
  }

  public inviteStartTime(): string {
    if (!this.invite || !this.invite.startDateTime) {
      return "";
    }

    const start = new Date(this.invite.startDateTime);
    if (isNaN(start.getTime())) {
      return "";
    }

    return start.toLocaleTimeString(this.browserLocale(), { hour: "2-digit", minute: "2-digit" });
  }

  public inviteLocation(): string {
    if (!this.invite) {
      return this.translate.instant("events.locationTbd");
    }

    if (this.invite.locationName) {
      return this.invite.locationName;
    }

    if (this.invite.playSpace && this.invite.playSpace.name) {
      return this.invite.playSpace.name;
    }

    return this.translate.instant("events.locationTbd");
  }

  public inviteAddress(): string {
    return this.invite && this.invite.playSpace && this.invite.playSpace.address
      ? this.invite.playSpace.address
      : "";
  }

  public showNameEditor(): boolean {
    return !this.savedName || this.editingName;
  }

  @HostListener("document:click", ["$event"])
  public handleDocumentClick(event: MouseEvent): void {
    if (!this.editingName || !this.savedResponse || this.submitting) {
      return;
    }

    const target = event.target as Node;
    if (this.nameEditorRef && this.nameEditorRef.nativeElement.contains(target)) {
      return;
    }

    this.cancelNameEdit();
  }

  public editName(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.editingName = true;
  }

  public cancelNameEdit(): void {
    this.name = this.savedName;
    this.nameErrorMsg = null;
    this.editingName = false;
  }

  public clearNameError(): void {
    this.nameErrorMsg = null;
  }

  public saveName(): void {
    if (!this.savedResponse || !this.hasNameChanged()) {
      return;
    }

    this.rsvp(this.savedResponse);
  }

  public participantInitial(participant: PublicEventInviteParticipantResponse): string {
    const name = this.participantDisplayName(participant);
    return name
      ? name.trim().charAt(0)
      : "?";
  }

  public participantDisplayName(participant: PublicEventInviteParticipantResponse): string {
    if (this.isDefaultGuestOwnerName(participant)) {
      return this.translate.instant("guestOwner.participants.organizer");
    }

    return participant && participant.name ? participant.name : "";
  }

  public isMatchOwner(participant: PublicEventInviteParticipantResponse): boolean {
    return participant && participant.matchOwner === true;
  }

  public participantDescription(participant: PublicEventInviteParticipantResponse): string {
    if (this.isMatchOwner(participant)) {
      return this.translate.instant("common.manager");
    }

    const source = this.normalizedParticipantField(participant && participant.source);
    if (source === "MANAGER") {
      return this.translate.instant("guestOwner.addedByManager");
    }

    if (source === "INVITE") {
      return this.translate.instant("publicInvite.participants.joinedFromInvite");
    }

    return this.translate.instant("publicInvite.participants.participant");
  }

  public canSubmitResponse(response: PublicEventRsvpResponseType): boolean {
    return !this.inviteUnavailable && !this.submitting && !!(this.name || "").trim() && (
      this.savedResponse !== response || this.hasNameChanged()
    );
  }

  public responseButtonDisabled(): boolean {
    return this.inviteUnavailable || this.submitting;
  }

  public createProfile(): void {
    this.guestUpgradeClaimService.clearPendingUpgrade();
    if (this.savedResponse) {
      this.saveRsvp(this.savedResponse, true, true);
      return;
    }

    this.authenticateFromInvite(true);
  }

  public authenticateFromInvite(signup = false): void {
    this.auth.loginWithRedirect({
      appState: { target: "/join/" + encodeURIComponent(this.inviteToken) },
      authorizationParams: signup ? { screen_hint: "signup" } : {}
    }).subscribe({ error: () => {
      this.errorMsg = this.translate.instant("publicInvite.errors.linkUnavailable");
    } });
  }

  private navigateToGuestUpgrade(): void {
    this.router.navigate(["/guest-upgrade"], {
      queryParams: {
        mode: "public-invite",
        inviteToken: this.inviteToken
      }
    });
  }

  public openChatBridge(): void {
    this.chatBridgeVisible = true;
    this.loadPublicChatMessages();
  }

  public closeChatBridge(): void {
    this.chatBridgeVisible = false;
  }

  public createProfileFromChat(): void {
    this.closeChatBridge();
    this.createProfile();
  }

  public chatMessageSender(message: PublicEventInviteChatMessageResponse): string {
    return message && message.senderName
      ? message.senderName
      : this.translate.instant("players.player");
  }

  public chatMessageText(message: PublicEventInviteChatMessageResponse): string {
    if (message && message.deleted) {
      return this.translate.instant("guestOwner.chat.deletedMessage");
    }

    return message && message.text ? message.text : "";
  }

  public chatMessageTime(message: PublicEventInviteChatMessageResponse): string {
    const timestamp = message && (message.createdAt || message.created);
    if (!timestamp) {
      return "";
    }

    const created = new Date(timestamp);
    if (isNaN(created.getTime())) {
      return "";
    }

    return created.toLocaleTimeString(this.browserLocale(), { hour: "2-digit", minute: "2-digit" });
  }

  public androidDownloadUrl(): string {
    if (!PublicEventInviteComponent.ANDROID_APP_URL) return "";
    const referrer = new URLSearchParams({
      utm_source: "public_invite",
      utm_medium: "web",
      utm_campaign: "match_invite",
      deep_link: this.matchLinkUrl()
    }).toString();

    return `${PublicEventInviteComponent.ANDROID_APP_URL}&referrer=${encodeURIComponent(referrer)}`;
  }

  public iosDownloadUrl(): string {
    if (!PublicEventInviteComponent.IOS_APP_URL) return "";
    const campaign = this.inviteToken ? `public_invite_${this.inviteToken}` : "public_invite";
    const params = new URLSearchParams({
      ct: campaign,
      mt: "8"
    }).toString();

    return `${PublicEventInviteComponent.IOS_APP_URL}?${params}`;
  }

  public matchLinkUrl(): string {
    return nativeAppUrl(`join/${encodeURIComponent(this.inviteToken || "")}`);
  }

  public continueInMobileApp(): void {
    if (this.openingMobileApp) {
      return;
    }
    if (!this.guestUpgradeClaimService.hasPublicInviteParticipantClaim(this.inviteToken)) {
      window.location.href = this.matchLinkUrl();
      return;
    }
    this.openingMobileApp = true;
    this.guestUpgradeClaimService.issuePublicInviteContinuation(this.inviteToken).subscribe(
      (handoff) => {
        this.openingMobileApp = false;
        this.startGuestContinuationObserver(this.invite.eventId);
        window.location.href = nativeAppUrl(`guest-continuation?handoff=${encodeURIComponent(handoff.handoffCode)}`);
      },
      () => {
        this.openingMobileApp = false;
        this.errorMsg = this.translate.instant("publicInvite.appDownload.continueFailed");
      }
    );
  }

  public openClaimedMatchInApp(): void {
    if (!this.invite || !this.invite.eventId) {
      return;
    }
    this.guestUpgradeClaimService.claimedMatchAppUrl("PUBLIC_INVITE_PARTICIPANT", this.invite.eventId)
      .subscribe({
        next: (url) => window.location.href = url,
        error: () => this.errorMsg = this.translate.instant("publicInvite.appDownload.continueFailed")
      });
  }

  private startGuestContinuationObserver(eventId: string): void {
    this.stopGuestContinuationObserver();
    this.guestContinuationClaimed = this.guestUpgradeClaimService.continuationWasClaimed(
      "PUBLIC_INVITE_PARTICIPANT",
      eventId
    );
    if (!this.guestUpgradeClaimService.hasContinuationObserver("PUBLIC_INVITE_PARTICIPANT", eventId)) {
      return;
    }
    this.guestContinuationObserverError = false;
    this.guestContinuationObservation = this.guestContinuationObserverService
      .observe("PUBLIC_INVITE_PARTICIPANT", eventId)
      .subscribe((observation) => {
        if (observation.error) {
          this.guestContinuationObserverError = true;
          return;
        }
        this.guestContinuationObserverError = false;
        if (observation.status && observation.status.status === "REVOKED") {
          this.guestContinuationClaimed = false;
          this.router.navigate(["/"]);
          return;
        }
        if (observation.status && observation.status.status === "CLAIMED") {
          this.guestContinuationClaimed = true;
        }
      });
  }

  public retryGuestContinuationObserver(): void {
    if (this.invite && this.invite.eventId) {
      this.startGuestContinuationObserver(this.invite.eventId);
    }
  }

  private stopGuestContinuationObserver(): void {
    if (this.guestContinuationObservation) {
      this.guestContinuationObservation.unsubscribe();
      this.guestContinuationObservation = null;
    }
  }

  public dismissMatchTimeChanged(): void {
    this.matchTimeChanged = false;
  }

  public dismissMatchLocationChanged(): void {
    this.matchLocationChanged = false;
  }

  public comingParticipants(): PublicEventInviteParticipantResponse[] {
    return this.participants.filter((participant) => participant.response === "COMING");
  }

  public joinedTeamParticipants(): PublicEventInviteParticipantResponse[] {
    return this.participants.filter((participant) => participant.response === "JOIN_TEAM");
  }

  public notComingParticipants(): PublicEventInviteParticipantResponse[] {
    return this.participants.filter((participant) => participant.response === "NOT_COMING");
  }

  public comingParticipantItems(): GuestParticipantListItem[] {
    return this.participantItems(this.comingParticipants());
  }

  public joinedTeamParticipantItems(): GuestParticipantListItem[] {
    return this.participantItems(this.joinedTeamParticipants(), true);
  }

  public notComingParticipantItems(): GuestParticipantListItem[] {
    return this.participantItems(this.notComingParticipants(), true);
  }

  public matchFull(): boolean {
    if (!this.invite || !this.invite.maxPlayers) {
      return false;
    }

    const confirmedCount = Math.max(Number(this.invite.confirmedCount || 0), this.comingParticipants().length);
    return confirmedCount >= Number(this.invite.maxPlayers);
  }

  public positiveResponseType(): PublicEventRsvpResponseType {
    if (this.savedResponse === "COMING") {
      return "COMING";
    }

    return this.matchFull() ? "JOIN_TEAM" : "COMING";
  }

  public unansweredCount(): number {
    if (!this.invite || !this.invite.maxPlayers) {
      return 0;
    }

    const knownResponses = this.comingParticipants().length + this.notComingParticipants().length;
    return Math.max(Number(this.invite.maxPlayers) - knownResponses, 0);
  }

  public responseButtonLabel(response: PublicEventRsvpResponseType): string {
    if (this.savedResponse === response && this.hasNameChanged()) {
      return this.translate.instant("publicInvite.response.saveChanges");
    }

    if (response === "COMING") {
      return this.savedResponse === "COMING"
        ? this.translate.instant("guestOwner.youAreComing")
        : this.translate.instant("guestOwner.iAmComing");
    }

    if (response === "JOIN_TEAM") {
      return this.savedResponse === "JOIN_TEAM"
        ? this.translate.instant("publicInvite.response.youJoinedTeam")
        : this.translate.instant("publicInvite.response.joinTeam");
    }

    return this.savedResponse === "NOT_COMING"
      ? this.translate.instant("guestOwner.youAreNotComing")
      : this.translate.instant("events.pass");
  }

  private participantItems(participants: PublicEventInviteParticipantResponse[], muted = false): GuestParticipantListItem[] {
    return participants.map((participant) => ({
      id: participant.participantId,
      name: this.participantDisplayName(participant),
      initial: this.participantInitial(participant),
      description: this.participantDescription(participant),
      muted
    }));
  }

  private isDefaultGuestOwnerName(participant: PublicEventInviteParticipantResponse): boolean {
    const name = participant && participant.name ? participant.name.trim().toLocaleLowerCase() : "";
    return this.isMatchOwner(participant) && PublicEventInviteComponent.DEFAULT_MANAGER_NAMES.has(name);
  }

  public savedResponseLabel(): string {
    if (this.savedResponse === "COMING") {
      return this.translate.instant("guestOwner.coming");
    }

    if (this.savedResponse === "JOIN_TEAM") {
      return this.translate.instant("publicInvite.response.joinedTeam");
    }

    return this.translate.instant("guestOwner.notComing");
  }

  public savedResponseTitle(): string {
    const displayName = (this.savedName || this.name || "").trim();
    if (this.savedResponse === "COMING") {
      return displayName
        ? this.translate.instant("publicInvite.response.youAreComingAs", { name: displayName })
        : this.translate.instant("events.confirmedComing");
    }

    if (this.savedResponse === "JOIN_TEAM") {
      return displayName
        ? this.translate.instant("publicInvite.response.youJoinedTeamAs", { name: displayName })
        : this.translate.instant("publicInvite.response.confirmedJoinedTeam");
    }

    return displayName
      ? this.translate.instant("publicInvite.response.youAreNotComingAs", { name: displayName })
      : this.translate.instant("events.confirmedNotComing");
  }

  public savedResponseTime(): string {
    if (!this.responseSavedAt) {
      return "";
    }

    return this.responseSavedAt.toLocaleTimeString(this.browserLocale(), { hour: "2-digit", minute: "2-digit" });
  }

  private loadParticipants(showLoading: boolean = true): void {
    if (!this.inviteToken || this.destroyed || this.inviteUnavailable) {
      return;
    }

    const sequence = ++this.participantsRequestSequence;
    const current = () => !this.destroyed && !this.inviteUnavailable && sequence === this.participantsRequestSequence;

    if (showLoading) {
      this.participantsLoading = true;
    }
    this.participantListMsg = null;
    console.info("[public-invite] Loading public participants.");
    this.guestOwnerService.publicInviteParticipants(this.inviteToken).pipe(
      timeout(15000),
      finalize(() => {
        if (current()) {
          this.participantsLoading = false;
        }
      })
    ).subscribe(
      (participants) => {
        if (!current()) return;
        console.info("[public-invite] Public participants loaded.", { count: participants ? participants.length : 0 });
        this.participants = this.normalizeParticipants(participants || []);
        this.syncStoredParticipantState();
      },
      (error) => {
        if (!current()) return;
        if (this.isInviteUnavailableError(error)) {
          this.markInviteUnavailable(this.translate.instant("publicInvite.errors.matchUnavailable"));
          return;
        }

        console.error("[public-invite] Public participants failed.", {
          status: error && error.status,
          name: error && error.name
        });
        this.participantListMsg = this.translate.instant("publicInvite.errors.participantListUnavailable");
      }
    );
  }

  private loadPublicChatMessages(): void {
    if (!this.inviteToken) {
      return;
    }

    this.chatPreviewLoading = true;
    this.chatPreviewUnavailable = false;
    this.guestOwnerService.publicInviteChatMessages(this.inviteToken).pipe(
      timeout(15000),
      finalize(() => {
        this.chatPreviewLoading = false;
      })
    ).subscribe(
      (messages) => {
        this.publicChatMessages = messages || [];
      },
      () => {
        this.publicChatMessages = [];
        this.chatPreviewUnavailable = true;
      }
    );
  }

  private errorMessage(error: HttpErrorResponse): string {
    if (error && error.status === 409 && error.error && error.error.code === "PARTICIPATION_CHANGED") {
      return this.translate.instant("guestOwner.notices.participationChangedMessage");
    }
    if (error && error.error) {
      if (typeof error.error === "string") {
        return error.error;
      }

      if (error.error.message) {
        return error.error.message;
      }

      if (error.error.error) {
        return error.error.error;
      }
    }

    return this.translate.instant("events.saveResponseError");
  }

  private markInviteUnavailable(message?: string): void {
    this.stopPublicInviteStream();
    this.inviteUnavailable = true;
    this.inviteUnavailableMsg = message || this.translate.instant("publicInvite.errors.matchUnavailable");
    this.invite = null;
    this.participants = [];
    this.participantsLoading = false;
    this.submitting = false;
    this.errorMsg = null;
    this.successMsg = null;
    this.participantListMsg = null;
    this.matchTimeChanged = false;
    this.matchLocationChanged = false;
    this.inviteRealtimeAlive = false;
  }

  private startPublicInviteStream(): void {
    this.stopPublicInviteStream();
    if (!this.inviteToken || this.inviteUnavailable || this.destroyed) {
      return;
    }

    this.closePublicInviteStream = this.guestOwnerService.openPublicInviteStream(
      this.inviteToken,
      (streamEvent) => this.handlePublicInviteStreamEvent(streamEvent),
      (error) => this.handlePublicInviteStreamError(error)
    );
  }

  private stopPublicInviteStream(): void {
    if (this.closePublicInviteStream) {
      this.closePublicInviteStream();
      this.closePublicInviteStream = null;
    }
  }

  private handlePublicInviteStreamEvent(streamEvent: PublicEventInviteStreamEvent): void {
    if (this.destroyed || !streamEvent) {
      return;
    }

    if (streamEvent.type === "connected") {
      this.inviteRealtimeAlive = true;
      this.inviteRealtimeError = false;
      this.loadParticipants(false);
      this.refreshInviteAfterReconnect();
      return;
    }

    if (streamEvent.type === "heartbeat") {
      this.inviteRealtimeAlive = true;
      return;
    }

    if (streamEvent.type === "participants.changed") {
      this.inviteRealtimeAlive = true;
      this.inviteRealtimeError = false;
      this.loadParticipants(false);
      return;
    }

    if (streamEvent.type === "event.updated") {
      this.inviteRefreshSequence++;
      this.inviteRealtimeAlive = true;
      this.inviteRealtimeError = false;
      this.applyInviteUpdate(streamEvent.data);
      return;
    }

    if (streamEvent.type === "event.deleted") {
      this.markInviteUnavailable(this.translate.instant("publicInvite.errors.matchUnavailable"));
    }
  }

  private handlePublicInviteStreamError(error: any): void {
    if (this.destroyed || this.inviteUnavailable) return;
    this.inviteRealtimeAlive = false;
    this.inviteRealtimeError = true;
    if (navigator.onLine) this.refreshInviteAfterReconnect();
    console.warn("[public-invite] Live invite stream failed; keeping manual loads as fallback.", {
      type: error && error.type
    });
  }

  @HostListener("window:online")
  public reconnectInvite(): void {
    if (this.invite && !this.guestContinuationClaimed && !this.inviteUnavailable && !this.destroyed) {
      this.loadParticipants(false);
      this.refreshInviteAfterReconnect();
      this.startPublicInviteStream();
    }
  }

  @HostListener("window:offline")
  public disconnectInvite(): void {
    if (this.destroyed || !this.invite) return;
    this.stopPublicInviteStream();
    this.inviteRealtimeAlive = false;
    this.inviteRealtimeError = true;
  }

  private refreshInviteAfterReconnect(): void {
    if (this.inviteRefreshInFlight || this.destroyed || this.inviteUnavailable) return;
    this.inviteRefreshInFlight = true;
    const sequence = ++this.inviteRefreshSequence;
    const token = this.inviteToken;
    const current = () => !this.destroyed && !this.inviteUnavailable && this.inviteToken === token
      && sequence === this.inviteRefreshSequence;
    this.guestOwnerService.previewInvite(token).pipe(timeout(15000),
      finalize(() => this.inviteRefreshInFlight = false)).subscribe({
      next: invite => {
        if (current()) this.applyInviteUpdate(invite);
      },
      error: error => {
        if (!current()) return;
        if (this.isInviteUnavailableError(error)) this.markInviteUnavailable(this.translate.instant("publicInvite.errors.matchUnavailable"));
        else this.inviteRealtimeError = true;
      }
    });
  }

  private applyInviteUpdate(payload: any): void {
    const updatedInvite = payload && payload.event ? payload.event : payload;
    if (!updatedInvite) {
      return;
    }

    if (updatedInvite.inviteActive === false) {
      this.markInviteUnavailable(this.translate.instant("publicInvite.errors.inviteInactive"));
      return;
    }

    const timeChanged = this.hasInviteTimeChanged(updatedInvite);
    const locationChanged = this.hasInviteLocationChanged(updatedInvite);
    this.invite = Object.assign({}, this.invite || {}, updatedInvite);

    this.matchTimeChanged = this.matchTimeChanged || timeChanged;
    this.matchLocationChanged = this.matchLocationChanged || locationChanged;
  }

  private hasInviteTimeChanged(updatedInvite: Partial<PublicEventInvitePreviewResponse>): boolean {
    if (!this.invite) {
      return false;
    }

    return (this.invite.startDateTime || "") !== (updatedInvite.startDateTime || "");
  }

  private hasInviteLocationChanged(updatedInvite: Partial<PublicEventInvitePreviewResponse>): boolean {
    if (!this.invite) {
      return false;
    }

    const currentPlaySpace = this.invite.playSpace || null;
    const updatedPlaySpace = updatedInvite.playSpace || null;
    return (this.invite.locationName || "") !== (updatedInvite.locationName || "")
      || (currentPlaySpace && currentPlaySpace.name || "") !== (updatedPlaySpace && updatedPlaySpace.name || "")
      || (currentPlaySpace && currentPlaySpace.address || "") !== (updatedPlaySpace && updatedPlaySpace.address || "");
  }

  private markRsvpSaved(response: PublicEventRsvpResponseType, name?: string): void {
    this.savedResponse = response;
    this.savedName = (name || this.name || "").trim();
    this.name = this.savedName;
    this.editingName = false;
    this.responseSavedAt = new Date();
  }

  public hasNameChanged(): boolean {
    return !!this.savedResponse && (this.name || "").trim() !== (this.savedName || "").trim();
  }

  private normalizeParticipants(participants: PublicEventInviteParticipantResponse[]): PublicEventInviteParticipantResponse[] {
    const byParticipantId: {[participantId: string]: PublicEventInviteParticipantResponse} = {};
    participants.forEach((participant) => {
      if (participant && participant.participantId) {
        byParticipantId[participant.participantId] = participant;
      }
    });
    return Object.keys(byParticipantId).map((participantId) => byParticipantId[participantId]);
  }

  private upsertParticipant(updatedParticipant: PublicEventInviteParticipantResponse): void {
    const participants = this.participants.filter((participant) => participant.participantId !== updatedParticipant.participantId);
    participants.push(updatedParticipant);
    this.participants = this.normalizeParticipants(participants);
  }

  private syncStoredParticipantState(): void {
    const participantId = this.storedParticipantId();
    if (!participantId) {
      return;
    }

    const storedParticipant = this.participants.find((participant) => participant.participantId === participantId);
    if (!storedParticipant) {
      return;
    }

    this.savedResponse = storedParticipant.response;
    this.savedName = this.savedName || storedParticipant.name || "";
    if (!this.name) {
      this.name = storedParticipant.name;
    }
  }

  private storedParticipantId(): string {
    return localStorage.getItem(this.participantStorageKey()) || "";
  }

  private storeParticipantId(participantId: string): void {
    localStorage.setItem(this.participantStorageKey(), participantId);
  }

  private storeParticipantClaim(
    rsvpResponse: PublicEventRsvpResponse,
    name: string,
    response: PublicEventRsvpResponseType
  ): void {
    if (!rsvpResponse || !rsvpResponse.guestParticipantToken) {
      return;
    }

    this.guestUpgradeClaimService.savePublicInviteParticipantClaim({
      inviteToken: this.inviteToken,
      participantId: rsvpResponse.participantId,
      guestParticipantToken: rsvpResponse.guestParticipantToken,
      eventId: rsvpResponse.eventId,
      name: rsvpResponse.name || name,
      response: rsvpResponse.response || response,
      updatedAt: new Date().toISOString()
    });
  }

  private clearStoredParticipantId(): void {
    localStorage.removeItem(this.participantStorageKey());
  }

  private participantStorageKey(): string {
    return `publicInviteParticipant:${this.inviteToken}`;
  }

  private isStoredParticipantRejected(error: HttpErrorResponse): boolean {
    return error && (error.status === 400 || error.status === 404);
  }

  private isInviteUnavailableError(error: HttpErrorResponse): boolean {
    return error && error.status === 404;
  }

  private currentTargetUrl(): string {
    return window.location.pathname + window.location.search + window.location.hash;
  }

  private currentInviteUrl(): string {
    if (typeof window === "undefined" || !window.location) {
      return `/join/${this.inviteToken || ""}`;
    }

    return `${window.location.origin}${this.currentTargetUrl()}`;
  }

  private normalizedParticipantField(value: string): string {
    return (value || "").trim().toUpperCase();
  }

  private browserLocale(): string {
    return this.localeService.currentLanguage === "hr" ? "hr-HR" : "en-US";
  }
}
